<template>
  <form class="space-y-6" @submit="onSubmit">
    <div class="flex justify-center p-4">
      <IconBeepaidCustomers />
    </div>

    <FormInputText name="name" label="Service Name" />

    <FormInputText name="description" label="Description" />

    <FormInputPrice
      name="cost"
      label="Price (leave as 0 if configurable)"
    />

    <div v-if="currentTenant?.includeVat">
      <div class="flex items-center justify-between">
        <FormFieldLabel> VAT (20%) </FormFieldLabel>

        <p>
          {{
            formatToCurrencyString((values.cost ?? 0) * 0.2)
          }}
        </p>
      </div>

      <div class="flex items-center justify-between">
        <FormFieldLabel> Total </FormFieldLabel>

        <p>
          {{
            formatToCurrencyString((values.cost ?? 0) * 1.2)
          }}
        </p>
      </div>
    </div>

    <FormSubmitButton
      class="w-full"
      :is-loading="isSubmitting"
    >
      {{ serviceId ? "Update Service" : "Create Service" }}
    </FormSubmitButton>
  </form>

  <ButtonSecondary
    v-if="serviceId"
    class="mt-4 w-full"
    @click="onDeleteService"
  >
    Delete Service
  </ButtonSecondary>

  <BaseConfirmation
    ref="deleteServiceConfirmationRef"
    title="Delete Service?"
    description="Once deleted a service is not recoverable, but payments made using this service will remain intact"
    confirm-label="Yes"
    cancel-label="Go Back"
  />
</template>

<script setup lang="ts">
import * as z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import type BaseConfirmation from "@/components/BaseConfirmation.vue"

const props = defineProps<{
  serviceId?: string
}>()

const router = useRouter()

const { currentTenant } = useCurrentTenant()

const createEditServiceFormSchema = z.object({
  name: z.string().min(1, "Name must not be empty"),
  description: z
    .string()
    .min(1, "Description must not be empty"),
  cost: z.number().nullable(),
})

const {
  handleSubmit,
  isSubmitting,
  resetForm,
  setErrors,
  values,
} = useForm({
  validationSchema: toTypedSchema(
    createEditServiceFormSchema
  ),
  initialValues: {
    name: "",
    description: "",
    cost: 0,
  },
})

const onSubmit = handleSubmit(async (params) => {
  try {
    props.serviceId
      ? await updateService(props.serviceId, params)
      : await createService(params)

    toast.success(
      props.serviceId
        ? "Service Updated!"
        : "Service Created!"
    )
    router.push({
      name: "servicesIndex",
    })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error(
      props.serviceId
        ? "Failed to update service"
        : "Failed to create service"
    )
  }
})

const isRetrievingService = ref(false)
onMounted(async () => {
  if (props.serviceId) {
    try {
      isRetrievingService.value = true
      resetForm({
        values: await retrieveService(props.serviceId),
      })
    } catch {
      toast.error("Service not found")
      router.push({
        name: "servicesIndex",
      })
    } finally {
      isRetrievingService.value = false
    }
  }
})

const deleteServiceConfirmationRef = useTemplateRef(
  "deleteServiceConfirmationRef"
)

const isDeleting = ref(false)
const onDeleteService = async () => {
  if (deleteServiceConfirmationRef.value === null) return
  if (!props.serviceId) return

  const { isCanceled } =
    await deleteServiceConfirmationRef.value.reveal()

  if (isCanceled) return

  isDeleting.value = true
  try {
    await deleteService(props.serviceId)
    toast.success("Service deleted")
    router.push({
      name: "servicesIndex",
    })
  } catch {
    toast.error("Failed to delete service")
  } finally {
    isDeleting.value = false
  }
}
</script>
